import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Box } from 'components/Box';
import { CloseButton, PrimaryButton } from 'components/Button';
import Icon from 'components/Icon/Icon';
import { Input } from 'components/Input/Input';
import Pill from 'components/Pill';
import { colors, textStyles, transition } from 'theme';
import { matchSize } from 'utils/responsive';

const SearchButton = styled(PrimaryButton)`
  width: 100%;
  border-radius: 4px;
`;
const InputContainer = styled(Box)`
  background: ${colors.platinum};
  border-radius: 4px;
  border: 1px solid transparent;
  box-shadow: 0 0 0 1px transparent inset;
  height: 100%;
  transition: all ${transition};
  will-change: background, border, box-shadow;
  cursor: text;

  &:focus-within {
    background: transparent;
    border: 1px solid ${colors.waterBlue};
    box-shadow: 0 0 0 1px ${colors.waterBlue} inset;

    &:hover {
      background: transparent;
    }
  }

  > div > div {
    width: 100%;
  }

  div {
    &:focus-within {
      background: transparent;
      border: none;
      box-shadow: none;
    }
  }

  input {
    padding: 0;
    border: none;
    box-shadow: none;
    padding-left: 16px;
    height: 100%;
    background: transparent;
    overflow: hidden;
    text-overflow: ellipsis;
    &::-ms-clear {
      width: 0;
      height: 0;
    }
    ${textStyles.bodySmall};
    width: 100%;

    &:focus {
      background: transparent;
      border: none;
      box-shadow: none;
      overflow: visible;
    }
    ${(props) =>
      props.suffix &&
      `
    padding-right: 45px;
  `}
  }
`;
type Props = {
  query: string;
  className?: string;
  placeholder?: string;
  xsPlaceholder?: string;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setQuery: (arg0: any) => void;
  clearQuery: () => void;
  search: () => void;
  'data-qa-id'?: string;
  isStatic?: boolean;
  pill?: string;
};

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
export const PostSearchInput = ({
  query,
  placeholder,
  xsPlaceholder,
  setQuery,
  clearQuery,
  search,
  'data-qa-id': qaId,
  isStatic,
  pill,
  ...props
}: Props) => {
  const [inputHasFocus, setInputHasFocus] = useState(false);
  const [buttonHasFocus, setButtonHasFocus] = useState(false);
  const [inputDisplay, setInputDisplay] = useState(pill || query);
  const [showPill, setShowPill] = useState(
    inputDisplay && inputDisplay.length > 0
  );
  const inputRef = useRef(null);
  useEffect(() => {
    if (query === '') {
      setShowPill(false);
      setInputDisplay('');
    }
  }, [query]);
  useEffect(() => {
    setShowPill(pill && pill.length > 0);

    if (pill && pill.length) {
      setInputDisplay(pill);
    }
  }, [pill]);

  const onChange = (e) => {
    setShowPill(false);
    setInputDisplay(e.target.value);

    if (e.target.value !== pill) {
      setQuery(e.target.value);
    }
  };

  const hasFocus = buttonHasFocus || inputHasFocus;

  const onFocus = () => {
    setInputHasFocus(true);
    setShowPill(false);
  };

  const onBlur = () => {
    setInputHasFocus(false);

    if (pill === inputDisplay) {
      setShowPill(true);
    }
  };

  const clearPill = (clearInput) => {
    setInputDisplay(clearInput ? '' : pill);
    setShowPill(false);
  };

  const onClickContainer = () => {
    if (showPill) {
      // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 0.
      clearPill();
    }

    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const onClickClear = (e) => {
    e.stopPropagation();
    clearQuery();
    setInputDisplay('');

    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();

    if (pill !== inputDisplay) {
      search();
    } else {
      setShowPill(true);
    }
  };

  const pillMode = showPill && inputDisplay && inputDisplay.length > 0;
  return (
    <Box
      width="100%"
      display="flex"
      alignItems="center"
      justifyContent="flex-end"
      component="form"
      onSubmit={onSubmit}
    >
      <InputContainer
        display="flex"
        alignItems="center"
        overflow="hidden"
        width={['100%', null, null, hasFocus || isStatic ? '100%' : 220]}
        height={50}
        $hasFocus={hasFocus}
        onClick={onClickContainer}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      >
        <Box
          position={pillMode ? 'absolute' : 'relative'}
          width={[
            '100%',
            null,
            null,
            hasFocus || isStatic ? 'calc(100% - 108px)' : '100%',
          ]}
          height="100%"
          flex="0 0 auto"
          display="flex"
          alignItems="center"
          opacity={pillMode ? 0 : 1}
          transition={`width ${transition}`}
          willChange="width"
        >
          <Input
            inputProps={{
              placeholder:
                matchSize({ xs: true }) && xsPlaceholder
                  ? xsPlaceholder
                  : placeholder,
              'aria-label': placeholder,
              ref: inputRef,
              value: inputDisplay,
              'data-qa-id': qaId,
              onFocus,
              onBlur,
              onChange,
            }}
            presentation={hasFocus ? 'default' : 'contrast'}
            prefix={
              <Box ml={12}>
                <Icon
                  name="search"
                  width={16}
                  height={16}
                  display="block"
                  fill={hasFocus ? colors.waterBlue : colors.greyishBrown}
                />
              </Box>
            }
            suffix={
              !pillMode && query !== '' ? (
                <Box width={45} position="relative" ml="auto">
                  <CloseButton
                    onClick={onClickClear}
                    data-qa-id="post-search-input-clear"
                    onMouseDown={(e) => e.preventDefault()}
                  />
                </Box>
              ) : null
            }
          />
        </Box>

        {pillMode ? (
          <Box
            position="relative"
            my="7px"
            pl={16}
            maxWidth="calc(100% - 30px)"
          >
            <Pill
              active
              text={inputDisplay}
              onClick={onClickClear}
              data-qa-id="post-search-input-clear"
            />
          </Box>
        ) : (
          <Box
            width={100}
            pl="8px"
            flex="0 0 auto"
            position="relative"
            display={['none', null, null, 'block']}
          >
            <SearchButton
              type="submit"
              data-qa-id="post-search-button"
              onFocus={() => setButtonHasFocus(true)}
              onBlur={() => setButtonHasFocus(false)}
            >
              {getText('Search')}
            </SearchButton>
          </Box>
        )}
      </InputContainer>
    </Box>
  );
};
